import { useState, useEffect } from 'react';
import { getRewardQueryV3 } from '@/services/claim';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { RewardType, Process, RewardQuery, ClaimOption } from '@/types/claim';
import { getTokenBalance } from '@/services/wallet';
import BigNumber from 'bignumber.js';
import { defaultCoinInfo } from '@/config';
import { useCurrentChainAddress } from '@/hooks/wallet';

/**
 * @file 控制领取奖励的步骤和状态
 */
export const useProcess = () => {
  const [process, setProcess] = useState(Process.Confirm);

  const onChangeProcess = (step: Process) => {
    setProcess(step);
  };

  return {
    process,
    onChangeProcess,
  };
};

export const useBalance = (option: Partial<ClaimOption>) => {
  const { poolInfo } = option as ClaimOption;
  const [balance, setBalance] = useState('');
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);

  const fetchBalance = async () => {
    const { success, result } = await getTokenBalance({
      chainId: poolInfo.chainId,
      tokenAddress: '',
      walletAddress,
    });

    if (success) {
      setBalance(result.balanceUsd);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  return {
    balance,
  };
};

export const useRewardList = (option: Partial<ClaimOption>) => {
  const { poolInfo } = option as ClaimOption;
  const [isLoading, setIsLoading] = useState(false);
  const [hasReward, setHasReward] = useState(false);
  const [rewardType, setRewardType] = useState(RewardType.Normal);
  const [rewardList, setRewardList] = useState<RewardQuery[]>([]);
  const [rewardFunds, setRewardFunds] = useState<string[]>([]);
  const { fetchCoinInfoCore } = useCoinInfoContext();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const rewardValue = rewardList.reduce((pre, cur) => {
    return BigNumber(pre)
      .plus(BigNumber(+(cur.amount || 0) * +(cur.usdPrice || 0)).toNumber())
      .toNumber();
  }, 0);

  const fetchData = async () => {
    setIsLoading(true);
    const { result, success } = await getRewardQueryV3({
      poolId: poolInfo.id,
      walletAddress,
      chainId: poolInfo.chainId,
    });

    if (!success || !result.length) return;

    const newRewardStatus = result?.[0].rewardStatus ?? RewardType.Normal;
    const newRewardList: RewardQuery[] = [];
    const newFunds: string[] = [];
    let haveReward = false;

    const coinInfoStore = await fetchCoinInfoCore([
      {
        chainCode: poolInfo.chainCode,
        contractAddresses: result.map((item) => item.contractAddress),
      },
    ]);

    result.forEach((item) => {
      if (item.rewardStatus === RewardType.Normal) haveReward = true;
      if (item.funds) {
        newFunds.push(item.funds);
      }
      const coinInfo =
        coinInfoStore[`${poolInfo.chainCode}${item.contractAddress}`] ||
        defaultCoinInfo;

      newRewardList.push({
        ...item,
        usdPrice: coinInfo.price,
        symbol: coinInfo.symbol ?? '',
        iconUrl: item.coinDetailJson?.iconNightUrl ?? '',
      });
    });

    setRewardFunds(newFunds);
    setRewardType(newRewardStatus);
    setRewardList(newRewardList);

    if (haveReward) {
      setHasReward(true);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    rewardFunds,
    isLoading,
    rewardList,
    rewardValue,
    rewardType,
    hasReward,
  };
};
